import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Truck, Loader2, CheckCircle2, ShieldCheck, Calendar, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Horas completas. Inicio: 08:00–15:00 (para garantizar ventana mínima de 3 h
// terminando como muy tarde a las 18:00). Fin: inicio+3 … 18:00.
const DAY_START = 8;
const DAY_END = 18;
const MIN_WINDOW = 3;
const hhmm = (h) => `${String(h).padStart(2, "0")}:00`;
const startOptions = Array.from({ length: DAY_END - MIN_WINDOW - DAY_START + 1 }, (_, i) => DAY_START + i);

const Label = ({ children, required }) => (
  <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">
    {children}{required && <span className="text-[#D95A2B]"> *</span>}
  </label>
);

const Field = ({ label, required, children, hint }) => (
  <div>
    <Label required={required}>{label}</Label>
    <div className="mt-1">{children}</div>
    {hint && <span className="block text-[11px] text-[#8A9098] mt-1">{hint}</span>}
  </div>
);

export default function Recogida() {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [f, setF] = useState({
    name: "", tax_id: "", phone: "", phone_alt: "", email: "", contact_person: "",
    address: "", postal_code: "", city: "", province: "", country: "España",
    pickup_date: "", start_time: "", end_time: "", notes: "", access_notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const endOptions = useMemo(() => {
    if (!f.start_time) return [];
    const sh = parseInt(f.start_time.slice(0, 2), 10);
    const out = [];
    for (let h = sh + MIN_WINDOW; h <= DAY_END; h++) out.push(h);
    return out;
  }, [f.start_time]);

  const onStart = (v) => {
    setF((p) => {
      const sh = parseInt(v.slice(0, 2), 10);
      // Si la hora de fin deja de ser válida, se limpia.
      const eh = p.end_time ? parseInt(p.end_time.slice(0, 2), 10) : null;
      const keepEnd = eh !== null && eh >= sh + MIN_WINDOW && eh <= DAY_END;
      return { ...p, start_time: v, end_time: keepEnd ? p.end_time : "" };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    const req = [
      ["name", "El nombre / razón social es obligatorio."],
      ["tax_id", "El NIF / DNI es obligatorio."],
      ["phone", "El teléfono es obligatorio."],
      ["email", "El email es obligatorio."],
      ["address", "La dirección de recogida es obligatoria."],
      ["postal_code", "El código postal es obligatorio."],
      ["city", "La población es obligatoria."],
      ["province", "La provincia es obligatoria."],
      ["country", "El país es obligatorio."],
      ["pickup_date", "Elige el día de recogida."],
    ];
    for (const [k, msg] of req) { if (!String(f[k]).trim()) return toast.error(msg); }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) return toast.error("Introduce un email válido.");
    if (f.pickup_date < today) return toast.error("No se pueden solicitar recogidas en fechas pasadas.");
    if (!f.start_time) return toast.error("Elige la hora de inicio.");
    if (!f.end_time) return toast.error("Elige la hora de fin.");
    const sh = parseInt(f.start_time.slice(0, 2), 10);
    const eh = parseInt(f.end_time.slice(0, 2), 10);
    if (eh - sh < MIN_WINDOW) return toast.error("La ventana de recogida debe ser de al menos 3 horas.");
    if (eh > DAY_END) return toast.error("La hora de fin no puede ser posterior a las 18:00.");

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/public/pickups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(f),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "No se pudo enviar la solicitud.");
      setDone(data);
    } catch (err) {
      toast.error(err.message || "Error al enviar la solicitud.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "var(--bg)" }}>
        <div className="ds-card p-8 text-center max-w-md w-full" style={{ boxShadow: "var(--shadow-lg)" }} data-testid="recogida-success">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#EFF3E6] mb-4">
            <CheckCircle2 size={30} className="text-[#6E8B3D]" />
          </div>
          <h3 className="font-display text-2xl font-bold mb-2">Recogida solicitada</h3>
          <p className="text-sm text-[#4B5158] leading-relaxed">
            Hemos recibido tu solicitud de recogida{done.code ? <> con la referencia <strong className="font-mono">{done.code}</strong></> : ""}.
            Te enviaremos la confirmación por email.
          </p>
          <button onClick={() => { setDone(null); setF((p) => ({ ...p, pickup_date: "", start_time: "", end_time: "", notes: "", access_notes: "" })); }}
            data-testid="recogida-new" className="ds-btn mt-6 justify-center">
            Solicitar otra recogida
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg)" }}>
      <div className="flex-1 flex items-start justify-center p-4 sm:py-10">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl text-white mb-4"
              style={{ background: "linear-gradient(160deg,#6E8B3D,#8AAF4E)", boxShadow: "inset 0 -2px 0 rgba(0,0,0,.15),0 1px 2px rgba(15,18,16,.08)" }}>
              <Truck size={22} strokeWidth={2.2} />
            </div>
            <div className="font-mono text-[11px] uppercase tracking-[.14em] text-[#3F5222] font-semibold mb-1">
              <span className="text-[#8A9098]">//</span>&nbsp;&nbsp;Recogida
            </div>
            <h1 className="font-display text-3xl font-black tracking-tight">Solicita una recogida</h1>
            <p className="text-sm text-[#4B5158] mt-1">Rellena tus datos, elige día y franja horaria y te recogeremos el equipo.</p>
          </div>

          <form onSubmit={submit} className="ds-card p-5 sm:p-6 space-y-6" style={{ boxShadow: "var(--shadow-lg)" }} data-testid="recogida-form">
            <div>
              <h3 className="font-mono text-[10px] uppercase tracking-[.14em] text-[#3F5222] font-semibold mb-3">Tus datos</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2"><Field label="Nombre / Razón social" required><input data-testid="recogida-name" className="ds-input" value={f.name} onChange={(e) => set("name", e.target.value)} /></Field></div>
                <Field label="NIF / DNI" required><input data-testid="recogida-taxid" className="ds-input mono" value={f.tax_id} onChange={(e) => set("tax_id", e.target.value)} /></Field>
                <Field label="Email" required><input data-testid="recogida-email" type="email" className="ds-input" value={f.email} onChange={(e) => set("email", e.target.value)} /></Field>
                <Field label="Teléfono" required><input data-testid="recogida-phone" className="ds-input" value={f.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
                <Field label="Teléfono alternativo"><input data-testid="recogida-phone-alt" className="ds-input" value={f.phone_alt} onChange={(e) => set("phone_alt", e.target.value)} /></Field>
                <div className="sm:col-span-2"><Field label="Persona de contacto"><input data-testid="recogida-contact" className="ds-input" value={f.contact_person} onChange={(e) => set("contact_person", e.target.value)} /></Field></div>
              </div>
            </div>

            <div>
              <h3 className="font-mono text-[10px] uppercase tracking-[.14em] text-[#3F5222] font-semibold mb-3">Dirección de recogida</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2"><Field label="Dirección" required><input data-testid="recogida-address" className="ds-input" value={f.address} onChange={(e) => set("address", e.target.value)} /></Field></div>
                <Field label="Código postal" required><input data-testid="recogida-postal" className="ds-input" value={f.postal_code} onChange={(e) => set("postal_code", e.target.value)} /></Field>
                <Field label="Población" required><input data-testid="recogida-city" className="ds-input" value={f.city} onChange={(e) => set("city", e.target.value)} /></Field>
                <Field label="Provincia" required><input data-testid="recogida-province" className="ds-input" value={f.province} onChange={(e) => set("province", e.target.value)} /></Field>
                <Field label="País" required><input data-testid="recogida-country" className="ds-input" value={f.country} onChange={(e) => set("country", e.target.value)} /></Field>
              </div>
            </div>

            <div>
              <h3 className="font-mono text-[10px] uppercase tracking-[.14em] text-[#3F5222] font-semibold mb-3 flex items-center gap-1.5"><Calendar size={13} /> Día y franja horaria</h3>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Día de recogida" required>
                  <input data-testid="recogida-date" type="date" min={today} className="ds-input" value={f.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} />
                </Field>
                <Field label="Hora de inicio" required>
                  <select data-testid="recogida-start" className="ds-select" value={f.start_time} onChange={(e) => onStart(e.target.value)}>
                    <option value="">—</option>
                    {startOptions.map((h) => <option key={h} value={hhmm(h)}>{hhmm(h)}</option>)}
                  </select>
                </Field>
                <Field label="Hora de fin" required hint="Ventana mínima de 3 horas">
                  <select data-testid="recogida-end" className="ds-select" value={f.end_time} disabled={!f.start_time} onChange={(e) => set("end_time", e.target.value)}>
                    <option value="">—</option>
                    {endOptions.map((h) => <option key={h} value={hhmm(h)}>{hhmm(h)}</option>)}
                  </select>
                </Field>
              </div>
              <p className="flex items-center gap-1.5 text-[11px] text-[#8A9098] mt-2"><Clock size={12} /> Franja de horas completas entre 08:00 y 18:00. Ejemplos: 09:00–12:00, 15:00–18:00.</p>
            </div>

            <div>
              <h3 className="font-mono text-[10px] uppercase tracking-[.14em] text-[#3F5222] font-semibold mb-3">Indicaciones</h3>
              <div className="grid gap-3">
                <Field label="Observaciones para mensajería"><textarea data-testid="recogida-notes" className="ds-textarea" rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
                <Field label="Indicaciones de acceso"><textarea data-testid="recogida-access" className="ds-textarea" rows={2} value={f.access_notes} onChange={(e) => set("access_notes", e.target.value)} /></Field>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-3 pt-1 border-t border-dashed border-[#D5D7CE]">
              <p className="hidden sm:flex items-center gap-1.5 text-[11px] text-[#8A9098] pt-3"><ShieldCheck size={13} /> Tus datos se tratan de forma confidencial.</p>
              <button type="submit" disabled={submitting} data-testid="recogida-submit" className="ds-btn primary w-full sm:w-auto justify-center mt-3 sm:mt-0" style={{ padding: "12px 20px", fontSize: 14 }}>
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Truck size={16} />}
                {submitting ? "Enviando…" : "Solicitar recogida"}
              </button>
            </div>
          </form>
        </div>
      </div>
      <footer className="text-center py-5 text-[11px] text-[#8A9098] font-mono uppercase tracking-[.12em]">
        Solicitud de recogida · sin necesidad de cuenta
      </footer>
    </div>
  );
}
